import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EventService, QueryState } from '@spartacus/core';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { DocumentsService } from '../../../../../core/catalog';
import {
  Article,
  Certification,
  CmsTabContainerComponent,
  EntityMap,
  PdpTabTypes,
  Product,
  ProductAttributeTable,
  ProductCertificateGroup,
  ProductDetailsTopic,
  ProductDocument,
} from '../../../../../core/model';
import { ProductCertificateGroupCustomizationEnum } from '../../../../../core/occ/occ-models/occ-generated.models';
import { ViewCatalogArticlesEvent } from '../../../../../core/user';
import { ItemListType } from '../../../../tracking/events/tracking-events';
import { AssortmentTable } from '../../../base/models';
import { ProductDetailsService } from '../../../base/services';

@Component({
  selector: 'py-pdp-sku-picker-tabs',
  templateUrl: './pdp-sku-picker-tabs.component.html',
  styleUrls: ['./pdp-sku-picker-tabs.component.scss'],
  providers: [ProductDetailsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdpSkuPickerTabsComponent implements OnInit {
  readonly productDetailsTopic = ProductDetailsTopic;
  product$ = new BehaviorSubject<Product>(undefined);
  article$ = new BehaviorSubject<Article>(undefined);
  activeTab$ = new BehaviorSubject<PdpTabTypes>(undefined);

  articleListTotalPageCount$ = new ReplaySubject<number>(1);
  articleListCurrentPage$ = new BehaviorSubject<number>(1);

  articleRefs$: Observable<string[]>;
  articleListQueryParams$: Observable<EntityMap<string>>;

  articleListPageCount: number = 12;

  PdpTabTypes: typeof PdpTabTypes = PdpTabTypes;

  assortmentTableState$: Observable<QueryState<AssortmentTable>>;
  materialComposition$: Observable<Certification[]>;
  washingInstructionIcons$: Observable<Certification[]>;
  washingInstructionGroups$: Observable<ProductCertificateGroup[]>;
  productCertificationGroups$: Observable<ProductCertificateGroup[]>;
  printingTechnologiesGroups$: Observable<ProductCertificateGroup[]>;
  productDocuments$: Observable<ProductDocument[]>;
  technicalAttributesTableState$: Observable<QueryState<ProductAttributeTable>>;

  @Input() set product(product: Product) {
    this.product$.next(product);
    this.articleListTotalPageCount$.next(Math.ceil(product?.articles?.length / this.articleListPageCount) + 1);
    this.productDetailsService.setProduct(product);
  }
  @Input() set article(article: Article) {
    this.article$.next(article);
  }
  @Input() set activeTab(activeTab: any) {
    this.activeTab$.next(activeTab);
  }

  @Input() tabContainers: CmsTabContainerComponent[];

  @Output() selectTab = new EventEmitter<PdpTabTypes>();

  ltemListTypePDP = ItemListType.PDP_ARTICLES_LIST;

  constructor(
    private activatedRoute: ActivatedRoute,
    private documentsService: DocumentsService,
    private eventService: EventService,
    private productDetailsService: ProductDetailsService
  ) {}

  ngOnInit(): void {
    this.productDocuments$ = combineLatest([this.product$, this.article$]).pipe(
      switchMap(([product, article]) => this.documentsService.getProductDocuments(product?.code, article?.code))
    );

    this.articleRefs$ = combineLatest([this.product$, this.articleListCurrentPage$]).pipe(
      filter(([product, _page]) => !!product),
      map(([product, page]) => product?.articles.slice(0, page * this.articleListPageCount)),
      tap((articles) => this.eventService.dispatch(new ViewCatalogArticlesEvent(articles)))
    );

    this.articleListQueryParams$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('mya')),
      map((myaValue) => (!!myaValue ? { mya: myaValue } : {}))
    );

    this.assortmentTableState$ = this.activeTab$.pipe(
      filter((activeTab) => activeTab === PdpTabTypes.Assortment),
      switchMap(() => this.productDetailsService.getAssortmentTableState())
    );

    this.technicalAttributesTableState$ = this.activeTab$.pipe(
      filter((activeTab) => activeTab === PdpTabTypes.TechnicalAttributes),
      switchMap(() => this.productDetailsService.getTechnicalAttributesState())
    );

    const certLabels$ = combineLatest([this.article$, this.product$]).pipe(
      switchMap(([article]) => (!!article ? of(article?.certLabels) : this.productDetailsService.getCertLabels())),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.materialComposition$ = certLabels$.pipe(
      map((certLabels) =>
        certLabels?.groups?.filter(
          (group) => group.customization === ProductCertificateGroupCustomizationEnum.MATERIAL_COMPOSITION
        )
      ),
      map((groups) => groups?.filter((group) => !!group.certificates?.length)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.washingInstructionGroups$ = certLabels$.pipe(
      map((certLabels) =>
        certLabels?.groups?.filter(
          (group) => group.customization === ProductCertificateGroupCustomizationEnum.WASHING_INSTRUCTIONS
        )
      ),
      map((groups) => groups?.filter((group) => !!group.certificates?.length)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.washingInstructionIcons$ = this.washingInstructionGroups$.pipe(
      map((groups) => groups?.flatMap((group) => group?.certificates) || []),
      map((certificates) => certificates?.filter((certificate) => !!certificate.icon))
    );

    this.productCertificationGroups$ = certLabels$.pipe(
      map((certLabels) =>
        certLabels?.groups?.filter((group) => group.customization === ProductCertificateGroupCustomizationEnum.DEFAULT)
      ),
      map((groups) => groups?.filter((group) => !!group.certificates?.length))
    );

    this.printingTechnologiesGroups$ = certLabels$.pipe(
      map((certLabels) =>
        certLabels?.groups?.filter(
          (group) => group.customization === ProductCertificateGroupCustomizationEnum.PRINTING_TECHNOLOGY
        )
      ),
      map((groups) => groups?.filter((group) => !!group.certificates?.length))
    );
  }

  getWashingInstructionIcons(instructions: Certification[]): Certification[] {
    return instructions.filter((instruction) => !!instruction.icon);
  }

  nextPage(page: number) {
    this.articleListCurrentPage$.next(page);
  }
}
